import {
    useRef,
    useState
} from 'react';

import { SimpleGrid, Text } from '@mantine/core';

import { useProductLineItems } from '../hooks/useProductLineItems.ts';
import { useProductLineItemsSelection } from '../hooks/useProductLineItemsSelection.ts';
import { FormDrawer } from '../drawers/FormDrawer.tsx';
import { update } from '../services/ProductLineItemsService.ts';

import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import { DataTableColumn } from "mantine-datatable";
import { DataTableTokenPagination } from "../../../components/DataTableTokenPagination.tsx";
import { ProductLineItem } from '../types';
import { useQueryClient } from '@tanstack/react-query';
import TableFormattedTimestamp from '../../../utils/TableFormattedTimestamp.tsx';

interface RecordsTableProps {
    productId?: string;
}

export const RecordsTable = ({ productId }: RecordsTableProps) => {
    const {selectedRecords, setSelectedRecords} = useProductLineItemsSelection();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<ProductLineItem | null>(null);
    const queryClient = useQueryClient();

    const {
        items,
        totalCount,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useProductLineItems(productId || '');

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const handleRecordClick = (record: ProductLineItem) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const handleDrawerSubmit = async (values: ProductLineItem) => {
        if (selectedRecord?.id) {
            const { product: _product, productFeature: _productFeature, createdByUsername: _createdByUsername, createdBy: _createdBy, ...rest } = values;
            await update(selectedRecord.id, rest as ProductLineItem);
            queryClient.invalidateQueries({ queryKey: ['product-line-items'] });
        }
    };

    const columns: DataTableColumn<ProductLineItem>[] = [
        {
            accessor: 'productFeature.name',
            title: 'Feature',
            render: (record: ProductLineItem) => (
                <Text
                    c="blue"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleRecordClick(record)}
                >
                    {record.productFeature?.name || 'Unknown Feature'}
                </Text>
            )
        },
        { accessor: 'position', title: 'Position' },
        {
            accessor: 'created',
            title: 'Created',
            render: (record: ProductLineItem) => (
                <TableFormattedTimestamp record={record} propertyName="created" />
            )
        },
    ];

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword} />

            <MantineDataTable
                columns={columns}
                records={items}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination totalCount={totalCount.toString()} nextToken={nextToken} handleLoadMore={fetchRecords} />

            <FormDrawer
                record={selectedRecord || {} as ProductLineItem}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};